/**
 * Shared top nav used on the marketing landing page AND the auth pages.
 * Keeps brand + CTAs consistent so login/signup feel like a continuation of
 * the marketing site, not a context switch.
 *
 * If a tenant's branding has resolved (custom domain → resolved logo+name),
 * the wordmark uses that instead of the platform's "Brightstars" mark.
 */

import { Logo } from './Logo.js'
import { useBranding } from '../lib/branding.js'

interface MarketingNavProps {
  /** When true, replaces the right-hand CTAs with a single "Sign in" hint. */
  variant?: 'marketing' | 'auth'
}

export function MarketingNav({ variant = 'marketing' }: MarketingNavProps) {
  const { branding } = useBranding()

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface/95 backdrop-blur">
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
        <a href="/" className="inline-flex items-center">
          {branding?.logoUrl ? (
            <span className="inline-flex items-center gap-2">
              <img
                src={branding.logoUrl}
                alt={branding.name}
                className="h-7 w-auto rounded"
              />
              <span className="text-base font-semibold text-fg">
                {branding.name}
              </span>
            </span>
          ) : branding ? (
            <span className="text-base font-semibold text-fg">{branding.name}</span>
          ) : (
            <Logo />
          )}
        </a>

        {variant === 'marketing' ? (
          <nav className="hidden md:flex items-center gap-8 text-sm text-fg-muted">
            <a href="/#features" className="hover:text-fg transition-colors">
              Features
            </a>
            <a href="/#pricing" className="hover:text-fg transition-colors">
              Pricing
            </a>
            <a href="/about" className="hover:text-fg transition-colors">
              About
            </a>
            <a href="/contact" className="hover:text-fg transition-colors">
              Contact
            </a>
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-8 text-sm text-fg-muted">
            <a href="/" className="hover:text-fg transition-colors">
              ← Home
            </a>
            <a href="/#features" className="hover:text-fg transition-colors">
              Features
            </a>
            <a href="/#pricing" className="hover:text-fg transition-colors">
              Pricing
            </a>
            <a href="/contact" className="hover:text-fg transition-colors">
              Contact
            </a>
          </nav>
        )}

        <div className="flex items-center gap-1 sm:gap-2">
          {variant === 'marketing' ? (
            <>
              {/* Discreet parent escape hatch. Keeps the buyer journey focused
                  on academy owners while letting invited parents find their way in. */}
              <a
                href="/login?role=parent"
                className="hidden sm:inline-flex items-center h-9 px-3 text-sm text-fg-subtle hover:text-fg transition-colors"
              >
                Parent sign in
              </a>
              <a
                href="/login"
                className="inline-flex items-center h-9 px-4 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] shadow-sm"
              >
                Sign in
              </a>
            </>
          ) : (
            <a
              href="/login"
              className="inline-flex items-center h-9 px-4 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] shadow-sm"
            >
              Sign in
            </a>
          )}
        </div>
      </div>
    </header>
  )
}
